import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';
import { Dayjs } from 'dayjs';
import {
  createSGRWarrantyRequestPruffConnect,
  getBusinessSGRWarrantyRequests,
} from 'lib/api/sgr';
import { getUser } from 'lib/api';
import { getUfValue, getDollarValue } from 'lib/api/cmf';
import { formatMoney, validaRut } from 'lib/functions/utils';
import { calculateDays } from 'lib/functions/dates';
import { formatAmountByCurrency } from 'lib/functions/utils';
import { CONVERSION_RATES } from 'lib/consts';
import { showAlert } from 'lib/functions/utils';

const PAGE_SIZE = 10;

interface NewQuotationState {
  type: string;
  quotedAmount: string;
  quotedCurrency: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  tenderId: string;
  principalRut: string;
  conversionRate: number;
}

const parseAmountInput = (value: string) => value.replace(/[^0-9]/g, '');

export const useWarranties = () => {
  const dispatch = useDispatch();
  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];
  const auth = useSelector((state: RootState) => state.auth);
  const userId = auth.user?.id;
  const [warranties, setWarranties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessIdentifier, setBusinessIdentifier] = useState<string | null>(
    null,
  );
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  // New quotation dialog state
  const [openNewQuotation, setOpenNewQuotation] = useState(false);
  const [newQuotation, setNewQuotation] = useState<NewQuotationState>({
    type: '',
    quotedAmount: '',
    quotedCurrency: 'CLP',
    startDate: null,
    endDate: null,
    tenderId: '',
    principalRut: '',
    conversionRate: 1,
  });

  // Form validation and calculation state
  const [convertedAmount, setConvertedAmount] = useState<number>(0);
  const [daysDifference, setDaysDifference] = useState<number>(0);
  const [rutError, setRutError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [rawAmount, setRawAmount] = useState('');
  const [conversionRates, setConversionRates] = useState(CONVERSION_RATES);

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchBusinessIdentifier = useCallback(async () => {
    const profile = await getUser(dispatch, { id: userId });
    if (!profile) throw new Error('Profile not found');
    const businessIdentifier = profile.company.taxIdentifier;
    setBusinessIdentifier(businessIdentifier);
  }, [dispatch, userId]);

  // Fetch warranties
  const fetchWarranties = useCallback(
    async (
      {
        page,
        pageSize,
      }: {
        page: number;
        pageSize: number;
      } = { page: 1, pageSize: PAGE_SIZE },
    ) => {
      try {
        setIsLoading(true);
        if (!businessIdentifier) return;
        const query = generateQuery({ page, pageSize });
        const response = await getBusinessSGRWarrantyRequests(
          dispatch,
          businessIdentifier,
          query,
        );
        if (response) {
          setWarranties(response.data);
          setPagination(response.pagination);
        }
      } catch (error) {
        console.error(error);
        setError(error instanceof Error ? error.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, businessIdentifier],
  );

  // Fetch conversion rates
  const fetchConversionRates = async () => {
    try {
      const [dollarResponse, ufResponse] = await Promise.all([
        getDollarValue(dispatch),
        getUfValue(dispatch),
      ]);

      setConversionRates({
        USD: dollarResponse?.dolar || 950,
        UF: ufResponse?.uf || 39000,
        CLP: 1,
      });
    } catch (error) {
      console.error('Error fetching conversion rates:', error);
      setConversionRates(CONVERSION_RATES);
    }
  };

  // Handle new quotation submission
  const createQuotation = async () => {
    try {
      setIsLoading(true);
      if (!businessIdentifier) throw new Error('Business identifier not found');
      const body = {
        type: newQuotation.type,
        quotedCurrency: newQuotation.quotedCurrency,
        quotedAmount: Number(newQuotation.quotedAmount),
        startDate: newQuotation.startDate,
        endDate: newQuotation.endDate,
        tenderId: newQuotation.tenderId,
        principalRut: newQuotation.principalRut,
        conversionRate: newQuotation.conversionRate,
        businessIdentifier,
      };
      const response = await createSGRWarrantyRequestPruffConnect(
        dispatch,
        secretId,
        body,
      );
      if (response) setOpenNewQuotation(false);
    } catch (error: any) {
      const message = error.response.data.message
        ? error.response.data.message
        : 'Error al crear la garantía';
      showAlert(dispatch, 'error', message);
      setError(message);
    } finally {
      resetForm();
      setIsLoading(false);
      fetchWarranties();
    }
  };

  // Form handlers
  const handleOpenNewQuotation = () => {
    setOpenNewQuotation(true);
  };

  const handleCloseNewQuotation = () => {
    setOpenNewQuotation(false);
  };

  const updateNewQuotation = (updates: Partial<NewQuotationState>) => {
    setNewQuotation((prev) => ({ ...prev, ...updates }));
  };

  const updateRawAmount = (value: string) => {
    const parsed = parseAmountInput(value);
    setRawAmount(parsed);
    setNewQuotation((prev) => ({
      ...prev,
      quotedAmount: parsed,
    }));
  };

  const resetForm = () => {
    setNewQuotation({
      type: '',
      quotedAmount: '',
      quotedCurrency: 'CLP',
      startDate: null,
      endDate: null,
      tenderId: '',
      principalRut: '',
      conversionRate: 1,
    });
    setRawAmount('');
    setConvertedAmount(0);
    setDaysDifference(0);
    setRutError(null);
    setDateError(null);
  };

  // Validation
  const isFormValid = Boolean(
    newQuotation.type &&
      newQuotation.quotedAmount &&
      newQuotation.startDate &&
      newQuotation.endDate &&
      newQuotation.tenderId &&
      newQuotation.principalRut &&
      !rutError &&
      !dateError,
  );

  // Effects for form logic
  useEffect(() => {
    fetchConversionRates();
  }, [dispatch]);

  useEffect(() => {
    if (rawAmount && newQuotation.quotedCurrency) {
      const amount = parseInt(rawAmount, 10) || 0;
      const rate =
        conversionRates[
          newQuotation.quotedCurrency as keyof typeof conversionRates
        ];
      setConvertedAmount(amount * rate);
      setNewQuotation((prev) => ({
        ...prev,
        conversionRate: rate,
      }));
    } else {
      setConvertedAmount(0);
    }
  }, [rawAmount, newQuotation.quotedCurrency, conversionRates]);

  useEffect(() => {
    if (newQuotation.startDate && newQuotation.endDate) {
      const start = newQuotation.startDate.toDate();
      const end = newQuotation.endDate.toDate();
      const days = calculateDays(start, end);
      setDaysDifference(days);
    } else {
      setDaysDifference(0);
    }
  }, [newQuotation.startDate, newQuotation.endDate]);

  useEffect(() => {
    if (newQuotation.startDate && newQuotation.endDate) {
      if (newQuotation.endDate.isBefore(newQuotation.startDate, 'day')) {
        setDateError(
          'La fecha de término debe ser posterior a la fecha de inicio',
        );
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [newQuotation.startDate, newQuotation.endDate]);

  // Validate RUT
  useEffect(() => {
    if (newQuotation.principalRut && !validaRut(newQuotation.principalRut)) {
      setRutError('RUT inválido');
    } else {
      setRutError(null);
    }
  }, [newQuotation.principalRut]);

  useEffect(() => {
    fetchBusinessIdentifier();
  }, [fetchBusinessIdentifier]);

  // Fetch warranties when businessIdentifier is available
  useEffect(() => {
    if (businessIdentifier) {
      fetchWarranties();
    }
  }, [businessIdentifier, fetchWarranties]);

  return {
    // Warranties data
    warranties,
    isLoading,
    setIsLoading,
    error,
    fetchWarranties,

    // Dialog state
    openNewQuotation,
    handleOpenNewQuotation,
    handleCloseNewQuotation,

    // Form state
    newQuotation,
    updateNewQuotation,
    rawAmount,
    updateRawAmount,
    convertedAmount,
    daysDifference,
    rutError,
    dateError,
    isFormValid,

    // Formatted values
    formattedAmount: formatAmountByCurrency(
      rawAmount,
      newQuotation.quotedCurrency,
    ),
    formattedConvertedAmount: formatMoney(convertedAmount),
    formattedConversionRate: formatMoney(newQuotation.conversionRate),

    // Handlers
    createQuotation,
    resetForm,

    // Pagination state
    pagination,
    setPagination,
  };
};
